import { type FC, memo, type ReactNode } from "react";
import { Button } from "~/common/components/Button/Button";
import { useIsMinTabletSize } from "../hooks/useMediaQuery";
import { Dialog, DialogActions, DialogContent, type DialogProps, Stack } from "@mui/material";

interface Props {
  label?: ReactNode;
  okLabel: ReactNode;
  cancelLabel: ReactNode;
  onOk?: () => void;
  onCancel: () => void;
  children?: ReactNode;
  hideClose?: boolean;
  startIcon?: React.ReactNode;
}

/**
 * Use either label or children - label takes precedence over children
 */
const ConfirmationModal: FC<Props> = ({ label, okLabel, cancelLabel, onCancel, onOk, children, startIcon }) => {
  const isTablet = useIsMinTabletSize();

  const handleClose: DialogProps["onClose"] = (e: React.MouseEvent<HTMLAnchorElement>, reason) => {
    e.preventDefault();
    e.stopPropagation();
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return; // Prevent closing on outside click or escape key
    }
    onCancel();
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: "312px !important",
            [theme.breakpoints.up("desktop-s")]: {
              width: "720px !important",
            },
          }),
        },
      }}
    >
      <DialogContent
        sx={{
          overflow: "hidden",
        }}
      >
        {label ? <p className={"headline-small"}>{label}</p> : children}
      </DialogContent>
      <DialogActions>
        <Stack gap={1} direction={"row"}>
          <Button fullWidth={!isTablet} variant={"text"} onClick={onCancel} data-test-id={"confirmation-modal-cancel"}>
            {cancelLabel}
          </Button>
          <Button fullWidth={!isTablet} onClick={onOk} data-test-id={"confirmation-modal-ok"} startIcon={startIcon}>
            {okLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ConfirmationModal);
